/**
 * Marionette template loading polyfill.
 *
 * Because the Marionette version 3-to-4 upgrade broke the way we were
 * loading templates, this module basically allows us to keep using
 * the old way. And it's a little faster.
 *
 * Requires `window.Backbone` to be available.
 */

import Marionette from 'backbone.marionette';
import Radio from 'backbone.radio';
import _ from 'underscore';


const viewChannel = Radio.channel('view');


// Cache of all templates the client knows about.
// - Initial values are strings as imported from the "template.json" files.
// - Once accessed for the first time, values are saved as `_.template()` functions.
// - This is seeded with an "empty" template that returns an empty string to allow both
//     Marionette.View and CollectionView to be overridden.
const templateCache = {
    null: () => ''
};
viewChannel.reply('templateCache', () => templateCache);

// Returns the requested template, and converts from string to Underscore if that has not yet been done
viewChannel.reply('template', selector =>
{
    // There are legitimate cases where the template would not exist; return null in those cases
    if (!(selector in templateCache)) return null;

    // If it's still a string, convert it to an underscore template
    if (typeof templateCache[selector] === 'string')
        templateCache[selector] = _.template(templateCache[selector]);

    // Otherwise, it's already cached as Underscore, return that
    return templateCache[selector];
});

// Registers a series of templates in the cache; keys are IDs passed into `request.template`
viewChannel.on('templates:register', templates =>
{
    Object.assign(templateCache, templates);
    viewChannel.trigger('templates:changed');
});

// Unregisters a series of templates; ids is a single template ID or an array of them
viewChannel.on('templates:unregister', ids =>
{
    if (!Array.isArray(ids)) ids = [ids];

    for (const id of ids)
        delete templateCache[id];

    viewChannel.trigger('templates:changed');
});

// Patch for Marionette 3-to-4 upgrade removing `templateCache`
function newGetTemplate()
{
    if (typeof this.template === 'function')
    {
        // A template is simply a function, so if we already have a
        // function then we can return it right away.
        return this.template;
    }
    else
    {
        // Otherwise request the template from the view channel.
        return viewChannel.request('template', this.template);
    }
}
Marionette.View.prototype.getTemplate = newGetTemplate;
Marionette.CollectionView.prototype.getTemplate = newGetTemplate;
